
import { BarChart2, TrendingUp, TrendingDown, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LineChart from '@/components/dashboard/LineChart';
import BarChart from '@/components/dashboard/BarChart';

// Sample data for charts
const monthlyMetricsData = [
  { name: 'Jan', value: 42 },
  { name: 'Feb', value: 47 },
  { name: 'Mar', value: 53 },
  { name: 'Apr', value: 58 },
  { name: 'May', value: 62 },
  { name: 'Jun', value: 67 },
  { name: 'Jul', value: 72 },
];

const channelMetricsData = [
  { name: 'Organic', value: 35 },
  { name: 'Social', value: 25 },
  { name: 'Email', value: 20 },
  { name: 'Referral', value: 15 },
  { name: 'Direct', value: 30 },
];

const conversionData = [
  { name: 'Week 1', value: 2.4 },
  { name: 'Week 2', value: 2.8 },
  { name: 'Week 3', value: 3.2 },
  { name: 'Week 4', value: 3.7 },
  { name: 'Week 5', value: 3.5 },
  { name: 'Week 6', value: 3.9 },
  { name: 'Week 7', value: 4.2 },
];

const Performance = () => {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-muted-foreground" />
          <p className="text-muted-foreground">Analyze your startup's key metrics and trends.</p>
        </div>

        <div className="flex space-x-3">
          <Button variant="outline" size="sm">
            Last 30 Days
          </Button>
          <Button variant="outline" size="sm">
            Export
          </Button>
        </div>
      </div>
      
      {/* Main metrics cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        <div className="card-glass rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">User Growth</p>
              <div className="flex items-baseline">
                <span className="text-2xl font-semibold">23.5%</span>
                <span className="ml-2 text-sm font-medium text-emerald-600 flex items-center">
                  <TrendingUp size={14} className="mr-1" /> +5.2%
                </span>
              </div>
            </div>
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
              <Activity size={18} className="text-primary" />
            </div>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Growing faster than 85% of comparable startups
          </div>
        </div>
        
        <div className="card-glass rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Conversion Rate</p>
              <div className="flex items-baseline">
                <span className="text-2xl font-semibold">3.8%</span>
                <span className="ml-2 text-sm font-medium text-emerald-600 flex items-center">
                  <TrendingUp size={14} className="mr-1" /> +0.5%
                </span>
              </div>
            </div>
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
              <TrendingUp size={18} className="text-primary" />
            </div>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Steady improvement over the last 6 weeks
          </div>
        </div>
        
        <div className="card-glass rounded-xl p-5">
          <div className="flex items-center justify-between">
            <div className="space-y-1">
              <p className="text-sm text-muted-foreground">Churn Rate</p>
              <div className="flex items-baseline">
                <span className="text-2xl font-semibold">1.7%</span>
                <span className="ml-2 text-sm font-medium text-rose-600 flex items-center">
                  <TrendingDown size={14} className="mr-1" /> +0.2%
                </span>
              </div>
            </div>
            <div className="h-9 w-9 rounded-full bg-primary/10 flex items-center justify-center">
              <BarChart2 size={18} className="text-primary" />
            </div>
          </div>
          <div className="mt-3 text-xs text-muted-foreground">
            Slight increase - review retention strategies
          </div>
        </div>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <LineChart 
          data={monthlyMetricsData} 
          title="Monthly Growth Metrics" 
          description="User acquisition trend over time"
        />
        <BarChart 
          data={channelMetricsData} 
          title="Acquisition Channels" 
          description="Performance by channel"
        />
      </div>
      
      <div className="grid grid-cols-1 gap-5">
        <LineChart 
          data={conversionData} 
          title="Conversion Rate Trend" 
          description="Weekly conversion rate performance"
        />
      </div>
      
      {/* Insights section */}
      <div className="card-glass rounded-xl p-5 mt-5">
        <h3 className="text-lg font-semibold">Performance Insights</h3>
        <p className="text-sm text-muted-foreground mt-1">Actionable recommendations based on your data</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mt-5">
          <div className="bg-muted/50 p-4 rounded-lg">
            <h4 className="text-sm font-medium">Improve Organic Reach</h4>
            <p className="text-xs text-muted-foreground mt-2">
              Your SEO efforts are paying off with a 12% improvement. Continue optimizing content strategy.
            </p>
          </div>
          <div className="bg-muted/50 p-4 rounded-lg">
            <h4 className="text-sm font-medium">Reduce Onboarding Friction</h4>
            <p className="text-xs text-muted-foreground mt-2">
              User drop-off during signup is 15% higher than benchmark. Review the onboarding flow.
            </p>
          </div>
          <div className="bg-muted/50 p-4 rounded-lg">
            <h4 className="text-sm font-medium">Boost Email Campaigns</h4>
            <p className="text-xs text-muted-foreground mt-2">
              Email open rates are strong at 28%, but click-through could be improved with better CTA design.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Performance;
